import React from "react";
import { Box, Typography } from "@mui/material";
import { getNombreSector } from "../../calendar/utils/zonas";
import { formatCurrency } from "../../shared/utils/formatters";
import { Reservation } from "../types/Reservation";
import { isReservaEstacionamiento } from "../../calendar/components/EstacionamientoRow";
import {
  formatReservaFechas,
  _getTooltipStyles,
} from "../styles/reservationStyles";
import { useTheme } from "@mui/material/styles";
import { useReservationTooltip } from "../contexts/TooltipContext";
import { isInternalDepartment } from "../../calendar/utils/internalDepartments";

function getOrigenReservaLabel(reserva: Reservation): string {
  const origen = reserva.cliente?.origen_reserva;
  if (origen === "booking") return "Booking";
  if (origen === "airbnb") return "Airbnb";
  return "Directa";
}

const titleSx = {
  color: "white",
  fontSize: "0.95rem",
  fontWeight: 800,
  lineHeight: 1.2,
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
} as const;

const labelSx = {
  color: "rgba(255,255,255,0.68)",
  fontSize: "0.74rem",
  fontWeight: 700,
  lineHeight: 1.35,
  whiteSpace: "nowrap",
} as const;

const valueSx = {
  color: "rgba(255,255,255,0.92)",
  fontSize: "0.78rem",
  fontWeight: 500,
  lineHeight: 1.35,
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
} as const;

const badgeSx = (bgcolor: string) => ({
  px: 0.75,
  py: 0.15,
  borderRadius: 1,
  fontSize: "0.7rem",
  fontWeight: 800,
  lineHeight: 1.35,
  bgcolor,
  color: "white",
  flexShrink: 0,
});

const ReservationTooltip = () => {
  const { visible, tooltipData } = useReservationTooltip();
  const theme = useTheme();

  if (!tooltipData) return null;

  const { reserva, position } = tooltipData;
  const fechas = formatReservaFechas(reserva.entrada, reserva.salida);

  const renderRow = (label: string, value: React.ReactNode) => (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "64px minmax(0, 1fr)",
        gap: 0.9,
        alignItems: "baseline",
      }}
    >
      <Typography sx={labelSx}>{label}</Typography>
      <Typography sx={valueSx}>{value}</Typography>
    </Box>
  );

  const renderNote = (value: string) => (
    <Box sx={{ pt: 0.4 }}>
      <Typography sx={labelSx}>Notas</Typography>
      <Typography
        sx={{
          ...valueSx,
          mt: 0.25,
          whiteSpace: "normal",
          overflowWrap: "anywhere",
        }}
      >
        {value}
      </Typography>
    </Box>
  );

  const renderContent = () => {
    if (isReservaEstacionamiento(reserva)) {
      return (
        <Box sx={{ p: 1, minWidth: 220, maxWidth: 300 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              mb: 0.85,
            }}
          >
            <Typography sx={titleSx}>
              {reserva.nombre} {reserva.apellido}
            </Typography>
            <Box sx={badgeSx(theme.palette.primary.main)}>
              Estacionamiento
            </Box>
          </Box>
          <Box sx={{ display: "grid", gap: 0.45 }}>
            {renderRow(
              "Fechas",
              `${fechas.entrada.fecha} - ${fechas.salida.fecha}`
            )}
            {renderRow("Lugar", reserva.estacionamiento)}
            {renderRow("Total", formatCurrency(reserva.valor_total))}
            {reserva.departamento_asociado && (
              renderRow("Asociado", `Depto ${reserva.departamento_asociado}`)
            )}
          </Box>
        </Box>
      );
    }

    const valorTotal = Number((reserva as Reservation).valor_total) || 0;
    const pago = Number((reserva as Reservation).pago) || 0;
    const esUsoInterno = isInternalDepartment(reserva.departamento);
    const sectorLabel = esUsoInterno
      ? "Boedo"
      : getNombreSector(reserva.departamento);
    const origenReserva = getOrigenReservaLabel(reserva as Reservation);
    const sectorColor = esUsoInterno
      ? theme.palette.zonas.interno.border
      : parseInt(reserva.departamento) <= 18
      ? theme.palette.zonas.centro.border
      : theme.palette.zonas.loma.border;

    return (
      <Box sx={{ p: 1, minWidth: 220, maxWidth: 300 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            mb: 0.85,
          }}
        >
          <Typography sx={titleSx}>
            {reserva.nombre}
          </Typography>
          <Box sx={badgeSx(sectorColor)}>
            {sectorLabel}
          </Box>
        </Box>
        <Box sx={{ display: "grid", gap: 0.45 }}>
          {renderRow(
            "Fechas",
            `${fechas.entrada.fecha} - ${fechas.salida.fecha}`
          )}
          {renderRow("Depto", reserva.departamento)}
          {esUsoInterno ? (
            <Typography sx={{ ...valueSx, whiteSpace: "normal" }}>
              Reserva interna sin valor comercial
            </Typography>
          ) : (
            <>
              {renderRow("Origen", origenReserva)}
              {renderRow("Total", formatCurrency(valorTotal))}
              {pago > 0 && (
                renderRow("Pago", formatCurrency(pago))
              )}
            </>
          )}
          {reserva.notas && renderNote(reserva.notas)}
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        ..._getTooltipStyles(position, visible),
        visibility: tooltipData ? "visible" : "hidden",
      }}
    >
      {renderContent()}
    </Box>
  );
};

export default React.memo(ReservationTooltip);
